import React, { useRef } from 'react';
import {
  Datagrid,
  ReferenceField,
  TextField,
  useRefresh,
  useRecordContext,
  DateField,
  NumberField,
} from 'react-admin';

import Chip from '@material-ui/core/Chip';
import { makeStyles } from '@material-ui/core/styles';
import ListAltIcon from '@material-ui/icons/ListAlt';
import PrintIcon from '@material-ui/icons/Print';
import ReplayIcon from '@material-ui/icons/Replay';
import SendIcon from '@material-ui/icons/Send';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { useSnackbar } from 'notistack';

import { DialogoConfirmacao } from '../../Components';
import ActionsList from '../../Components/ReactAdmin/ActionsList';
import ActionsField from '../../Components/ReactAdmin/Fields/ActionsField';
import ListPageComum from '../../Components/ReactAdmin/List/ListPageComum';
import { ProtegerPagina } from '../../layout/VerificaPermissaoPage';
import { api } from '../../services';
import { formatCNPJ, formatCPF } from '../../utils';
import {
  PRIMARY_COLOR,
  STATUS_EMISSAO_NFE_PENDENTE,
  STATUS_EMISSAO_NFE_AUTORIZADA,
  STATUS_EMISSAO_NFE_AUTORIZADA_SEM_XML,
  STATUS_EMISSAO_NFE_CANCELADA,
  STATUS_EMISSAO_NFE_CONTINGENCIA,
  STATUS_EMISSAO_NFE_CONTINGENCIA_ENVIADA,
  STATUS_EMISSAO_NFE_DENEGADA,
  STATUS_EMISSAO_NFE_INDEFINIDA,
  STATUS_EMISSAO_NFE_PROCESSANDO,
  STATUS_EMISSAO_NFE_REJEITADA,
  STATUS_EMISSAO_NFE_DESCONHECIDO,
  STATUS_EMISSAO_NFE_REJEITADA_DEFINITIVO,
  STATUS_EMISSAO_NFE_REJEITADA_REPROCESSAMENTO,
} from '../../utils/constants';
import { PERMISSAO_TELA_NFES_LISTAR } from '../../utils/dadosTelas';
import DialogoPdfDanfeNfe from '../Vendas/DialogoPdfDanfeNfe';
import DialogoShowVenda from '../Vendas/DialogoShow';
import DialogoLogs from './DialogoLogs';
import DialogoLogsRejeitada from './DialogoLogsRejeitada';
import Filter from './Filter';

const PermissionsExpand = ({ record }) => {
  if (!record) return null;

  const header = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: 16,
    marginBottom: 20,
  };

  const container = {
    padding: 20,
    background: '#fafafa',
    borderRadius: 8,
  };

  const timeline = {
    position: 'relative',
    marginLeft: 20,
    borderLeft: '2px solid #ddd',
    paddingLeft: 20,
  };

  const item = {
    position: 'relative',
    marginBottom: 20,
  };

  const dot = (color) => ({
    position: 'absolute',
    left: -30,
    top: 4,
    width: 12,
    height: 12,
    borderRadius: '50%',
    background: color,
  });

  const title = {
    fontWeight: 600,
    fontSize: 14,
    marginBottom: 2,
  };

  const desc = {
    fontSize: 13,
    color: '#666',
  };

  const error = {
    ...desc,
    color: '#c62828',
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('pt-BR');
  };

  const card = {
    background: '#fff',
    padding: 14,
    borderRadius: 8,
    border: '1px solid #eee',
  };

  const label = {
    fontSize: 12,
    fontWeight: 600,
    color: '#666',
    marginBottom: 4,
    textTransform: 'uppercase',
  };

  const value = {
    fontSize: 15,
    fontWeight: 500,
    color: '#222',
    wordBreak: 'break-all',
  };

  const Info = ({ labelText, valueText }) => (
    <div style={card}>
      <div style={label}>{labelText}</div>
      <div style={value}>{valueText || '-'}</div>
    </div>
  );

  return (
    <div style={container}>
      {/* CABEÇALHO */}
      <div style={header}>
        <Info
          labelText="Chave de acesso"
          valueText={record.chave_acesso_nota_fiscal}
        />
        <Info labelText="Número NF-e" valueText={record.numero_nfce} />
        <Info labelText="Série" valueText={record.serie_nfce} />
        <Info
          labelText="CPF / CNPJ Cliente"
          valueText={
            record.cpf
              ? formatCPF(record.cpf)
              : record.cnpj
              ? formatCNPJ(record.cnpj)
              : '-'
          }
        />
        <Info labelText="Protocolo" valueText={record.nProt} />
        {record.tentativas_emissao_nfce > 0 && (
          <Info
            labelText="Tentativas de emissão"
            valueText={record.tentativas_emissao_nfce}
          />
        )}
        {record.tentativas_emissao_contingencia_nfe > 0 && (
          <Info
            labelText="Tentativas de contingência"
            valueText={record.tentativas_emissao_contingencia_nfe}
          />
        )}
        {record.tentativas_download_xml > 0 && (
          <Info
            labelText="Tentativas de download XML"
            valueText={record.tentativas_download_xml}
          />
        )}
      </div>

      <div style={timeline}>
        <div style={item}>
          <div style={dot('#2196f3')} />
          <div style={title}>Emissão da NF-e</div>
          <div style={desc}>Data: {formatDate(record.data_emissao)}</div>
        </div>

        {record.contingencia ? (
          <div style={item}>
            <div style={dot('#ff9800')} />
            <div style={title}>Entrou em Contingência</div>
            <div style={desc}>Data: {formatDate(record.data_contingencia)}</div>
            <div style={desc}>Motivo: {record.xJust || '-'}</div>
          </div>
        ) : null}

        {record.data_autorizacao ? (
          <div style={item}>
            <div style={dot('#4caf50')} />
            <div style={title}>NF-e Autorizada</div>
            <div style={desc}>Data: {formatDate(record.data_autorizacao)}</div>
            <div style={desc}>Protocolo: {record.protocolo || '-'}</div>
          </div>
        ) : null}

        {record.erro_sefaz && (
          <div style={item}>
            <div style={dot('#f44336')} />
            <div style={title}>Erro retornado pela SEFAZ</div>
            <div style={error}>{record.erro_sefaz}</div>
          </div>
        )}
      </div>
    </div>
  );
};

const StatusField = (props) => {
  const record = useRecordContext(props);
  const status_emissao_nfce = record
    ? record.record
      ? record.record.status_emissao_nfce
      : null
    : null;

  let color = '';
  let backgroundColor = '';
  let label = '';

  switch (status_emissao_nfce) {
    case STATUS_EMISSAO_NFE_PENDENTE: {
      color = '#856404';
      backgroundColor = '#fff3cd';
      label = 'Pendente';
      break;
    }

    case STATUS_EMISSAO_NFE_PROCESSANDO: {
      color = '#0c5460';
      backgroundColor = '#d1ecf1';
      label = 'Processando';
      break;
    }

    case STATUS_EMISSAO_NFE_AUTORIZADA: {
      color = '#155724';
      backgroundColor = '#d4edda';
      label = 'Autorizada';
      break;
    }

    case STATUS_EMISSAO_NFE_AUTORIZADA_SEM_XML: {
      color = '#004085';
      backgroundColor = '#cce5ff';
      label = 'Autorizada sem XML';
      break;
    }

    case STATUS_EMISSAO_NFE_CONTINGENCIA: {
      color = '#856404';
      backgroundColor = '#fff3cd';
      label = 'Contingência';
      break;
    }

    case STATUS_EMISSAO_NFE_CONTINGENCIA_ENVIADA: {
      color = '#856404';
      backgroundColor = '#ffeeba';
      label = 'Contingência enviada';
      break;
    }

    case STATUS_EMISSAO_NFE_CANCELADA: {
      color = '#383d41';
      backgroundColor = '#e2e3e5';
      label = 'Cancelada';
      break;
    }

    case STATUS_EMISSAO_NFE_DENEGADA: {
      color = '#721c24';
      backgroundColor = '#f8d7da';
      label = 'Denegada';
      break;
    }

    case STATUS_EMISSAO_NFE_REJEITADA: {
      color = '#721c24';
      backgroundColor = '#f5c6cb';
      label = 'Rejeitada';
      break;
    }

    case STATUS_EMISSAO_NFE_REJEITADA_DEFINITIVO: {
      color = '#721c24';
      backgroundColor = '#f5c6cb';
      label = 'Rejeitada Definitivo';
      break;
    }

    case STATUS_EMISSAO_NFE_INDEFINIDA: {
      color = '#6c757d';
      backgroundColor = '#e2e3e5';
      label = 'Indefinida';
      break;
    }

    case STATUS_EMISSAO_NFE_DESCONHECIDO: {
      color = '#6c757d';
      backgroundColor = '#e2e3e5';
      label = 'Desconhecido';
      break;
    }

    case STATUS_EMISSAO_NFE_REJEITADA_REPROCESSAMENTO: {
      color = '#c28086';
      backgroundColor = '#b1a2a4';
      label = 'Rejeitada Reprocessamento';
      break;
    }

    default:
      break;
  }

  return (
    <Chip
      size="small"
      label={label}
      style={{
        backgroundColor,
        color,
        fontWeight: 600,
      }}
    />
  );
};

const useStyles = makeStyles((theme) => ({
  dataHeader: {
    flex: 1,
    backgroundColor: PRIMARY_COLOR,
    padding: '10px',
    color: 'white',
    fontWeight: 500,
    lineHeight: '1.5rem',
    fontSize: '0.875rem',
    borderBottom: '1px solid rgba(224, 224, 224, 1)',
    borderSpacing: 0,
    borderCollapse: 'collapse',
    // o TableSortLabel do MUI força a cor do texto para preto nos estados
    // hover/focus/ativo, o que apaga o label sobre o fundo primário.
    // A seta herda a cor do label (currentColor), então basta fixar o root.
    '& .MuiTableSortLabel-root': {
      color: 'white',
    },
    '& .MuiTableSortLabel-root:hover, & .MuiTableSortLabel-root:focus': {
      color: 'white',
    },
    '& .MuiTableSortLabel-root.MuiTableSortLabel-active': {
      color: 'white',
    },
  },
  dataCell: {
    flex: 1,
    padding: '10px',
    fontWeight: 500,
    lineHeight: '1.5rem',
    fontSize: '0.875rem',
    borderSpacing: 0,
    borderCollapse: 'collapse',
  },
}));

const List = (props) => {
  const refresh = useRefresh();
  const classes2 = useStyles();
  const refDialogoShowVenda = useRef(null);
  const refDialogoPdfDanfeNfe = useRef(null);
  const refDialogoLogsRejeitada = useRef(null);
  const refDialogoLogs = useRef(null);
  const refDialogoConfirmacao = useRef(null);
  const { enqueueSnackbar } = useSnackbar();

  const toolbarActions = <ActionsList {...props} />;

  function handleShow(id) {
    if (refDialogoShowVenda.current) {
      refDialogoShowVenda.current.handleOpen(id);
    }
  }

  function handlePdfDanfeNfe(id) {
    if (refDialogoPdfDanfeNfe.current) {
      refDialogoPdfDanfeNfe.current.handleOpen(id);
    }
  }

  async function concertarRejeitada(dados) {
    const response = await api.put(`/reprocessar_nfce_rejeitada/${dados.id}`);

    const { status, data } = response.data;

    if (status) {
      if (data) {
        if (refDialogoLogs.current) {
          refDialogoLogs.current.handleOpen(
            {
              retorno: JSON.stringify(data.retorno, null, 2),
              envio: JSON.stringify(data.nfce, null, 2),
              inutilizacao: JSON.stringify(data.responseInutilizacao, null, 2),
            },
            dados,
          );
        }
      }
    } else {
      enqueueSnackbar('erro doido', {
        variant: 'error',
      });
    }

    refresh();
  }

  function handleReenviarNfeRejeitada({ id }) {
    if (refDialogoConfirmacao.current) {
      refDialogoConfirmacao.current.handleOpen(
        'Reenviar NF-e rejeitada',
        'A NF-e voltará para a fila de emissão e será transmitida de novo à SEFAZ. Deseja continuar?',
        { id },
      );
    }
  }

  async function handleConfirmaReenvio({ id }) {
    try {
      const response = await api.put(`/reenviar_nfe_rejeitada/${id}`);

      const { status, message } = response.data;

      enqueueSnackbar(
        status ? 'NF-e devolvida para a fila de emissão!' : `${message}`,
        {
          variant: status ? 'success' : 'error',
        },
      );
    } catch (e) {
      enqueueSnackbar('Erro ao reenviar a NF-e.', {
        variant: 'error',
      });
    }

    refresh();
  }

  function parsificar(dados) {
    let parsed = dados;
    try {
      while (typeof parsed === 'string') {
        parsed = JSON.parse(parsed);
      }
    } catch {
      // se falhar, provavelmente é stringificado
    }

    return parsed;
  }

  async function verLogsRejeitadas(dados) {
    const response = await api.get(`/logs_nfce_rejeitada/${dados.id}`);
    const { retorno, envio, erro } = response.data;

    if (refDialogoLogsRejeitada.current) {
      refDialogoLogsRejeitada.current.handleOpen(
        {
          retorno: JSON.stringify(parsificar(retorno), null, 2),
          envio: JSON.stringify(parsificar(envio), null, 2),
          erro: JSON.stringify(parsificar(erro), null, 2),
        },
        dados,
      );
    }
  }

  const renderGrid = ({ listProps, classes, listContext, ...rest }) => (
    <>
      <Datagrid {...props} {...rest}>
        <StatusField
          label="Status Emissão"
          source="status_emissao_nfce"
          sortable={false}
          headerClassName={classes2.dataHeader}
          cellClassName={classes2.dataCell}
        />
        <TextField
          source="id"
          label="Id da Venda"
          headerClassName={classes2.dataHeader}
          cellClassName={classes2.dataCell}
          sortable={false}
        />
        {/* razao_social e nome_fantasia não existem em `vendas`, só no cadastro
            do cliente - por isso as duas colunas vêm de uma referência. Como
            apontam para o mesmo resource e os mesmos ids, o react-admin resolve
            as duas com um único getMany por página. */}
        <ReferenceField
          label="Razão Social"
          source="cliente_id"
          reference="clientes"
          link={false}
          headerClassName={classes2.dataHeader}
          cellClassName={classes2.dataCell}
          sortable={false}
        >
          <TextField source="razao_social" />
        </ReferenceField>
        <ReferenceField
          label="Nome Fantasia"
          source="cliente_id"
          reference="clientes"
          link={false}
          headerClassName={classes2.dataHeader}
          cellClassName={classes2.dataCell}
          sortable={false}
        >
          <TextField source="nome_fantasia" />
        </ReferenceField>
        <TextField
          source="numero"
          label="Número da Venda"
          headerClassName={classes2.dataHeader}
          cellClassName={classes2.dataCell}
          sortable={false}
        />
        <DateField
          source="data"
          label="Data da Venda"
          showTime
          headerClassName={classes2.dataHeader}
          cellClassName={classes2.dataCell}
        />
        <DateField
          source="data_emissao"
          label="Data da Emissão"
          showTime
          headerClassName={classes2.dataHeader}
          cellClassName={classes2.dataCell}
        />
        <NumberField
          source="valor"
          locales="pt-BR"
          options={{
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
            style: 'currency',
            currency: 'BRL',
          }}
          label="Valor Total"
          headerClassName={classes2.dataHeader}
          cellClassName={classes2.dataCell}
        />
        <ActionsField
          textAlign="right"
          label="Ações"
          sortable={false}
          headerClassName={classes2.dataHeader}
          cellClassName={classes2.dataCell}
          actions={[
            {
              title: 'Imprimir Danfe',
              icon: <PrintIcon />,
              onClick: async (r) => {
                handlePdfDanfeNfe(r.id);
              },
            },
            {
              title: 'Detalhes da Venda',
              icon: <VisibilityIcon />,
              onClick: async (r) => {
                handleShow(r.id);
              },
            },
            {
              title: 'Reenviar NF-e',
              icon: <SendIcon />,
              onClick: async (r) => {
                handleReenviarNfeRejeitada(r);
              },
              disabled: () => false,
              // Venda cancelada pode continuar marcada como rejeitada quando a
              // rejeição veio antes de a numeração ser alocada - nesse caso o
              // cancelamento não passa por marcarNotaCancelada. O backend
              // também barra, isto aqui só evita oferecer a ação.
              show: (r) =>
                !r.cancelado &&
                (r.status_emissao_nfce === STATUS_EMISSAO_NFE_REJEITADA ||
                  r.status_emissao_nfce ===
                    STATUS_EMISSAO_NFE_REJEITADA_DEFINITIVO),
            },
            {
              title: 'Reprocessar Rejeitada',
              icon: <ReplayIcon />,
              onClick: async (r) => {
                concertarRejeitada(r);
              },
              disabled: () => true,
              show: (r) =>
                r.status_emissao_nfce === STATUS_EMISSAO_NFE_REJEITADA,
            },
            {
              title: 'Logs da Rejeição',
              icon: <ListAltIcon />,
              onClick: async (r) => {
                verLogsRejeitadas(r);
              },
              disabled: () => false,
              show: (r) =>
                r.status_emissao_nfce === STATUS_EMISSAO_NFE_REJEITADA,
            },
          ]}
        />
      </Datagrid>
      <DialogoShowVenda ref={refDialogoShowVenda} />
      <DialogoPdfDanfeNfe ref={refDialogoPdfDanfeNfe} />
      <DialogoLogsRejeitada ref={refDialogoLogsRejeitada} />
      <DialogoLogs ref={refDialogoLogs} />
      <DialogoConfirmacao
        ref={refDialogoConfirmacao}
        handleConfirma={handleConfirmaReenvio}
      />
    </>
  );

  return (
    <ProtegerPagina permissao={PERMISSAO_TELA_NFES_LISTAR}>
      <ListPageComum
        title="NFEs"
        filters={<Filter />}
        toolbarActions={toolbarActions}
        renderGrid={(props) =>
          renderGrid({ ...props, expand: <PermissionsExpand /> })
        }
        perPage={50}
        sort={{ field: 'data', order: 'DESC' }}
        listProps={props}
        filterDefaultValues={{
          periodo: {
            start: new Date(),
            end: new Date(),
          },
        }}
      />
    </ProtegerPagina>
  );
};

export default List;
